import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  GridCellParams,
  GridColDef,
  GridRowClassNameParams
} from "@mui/x-data-grid";
import { reduce } from "et3-type-guards";
import { fontWeights } from "et3-ui-library";
import { DataTable } from "et3-ui-library/mui";
import { withLiteObserverAndTheme } from "../et3-enhanced-render";
import { TAdminTable } from "../../Types";
import i18n from "../../constants/i18n";
import getCoreHistoryStore from "../../stores/core/core-history-store";
import getCoreUIStore from "../../stores/core/core-ui-store";

const KhattabatDataTable = withLiteObserverAndTheme<{
  columns: GridColDef[];
  rows: Record<string, any>[];
  title: string;
  titleKey: TAdminTable;
  shouldBeFlagged: boolean;
  getRowClassName?: (params: GridRowClassNameParams) => string;
  getCellClassName?: (params: GridCellParams) => string;
}>((props) => {
  const {
    columns,
    rows,
    title,
    titleKey,
    shouldBeFlagged,
    getRowClassName,
    getCellClassName
  } = props;
  const tableRef = React.useRef<HTMLDivElement>(null);
  const hasBeenSeen = React.useRef(false);

  React.useEffect(() => {
    hasBeenSeen.current = false;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting && !hasBeenSeen.current) {
          await getCoreHistoryStore().logAdminGeneratedTable(titleKey);
          hasBeenSeen.current = true;
          observer.disconnect();
        }
      },
      {
        root: null,
        threshold: 0
      }
    );

    const tableContainer = tableRef.current;
    if (tableContainer) {
      requestAnimationFrame(() => {
        const firstRowVisible =
          tableContainer.querySelector(".MuiDataGrid-row");
        if (firstRowVisible) {
          observer.observe(firstRowVisible);
        }
      });
    }

    return () => observer.disconnect();
  }, [rows]);

  return (
    <Paper
      ref={tableRef}
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        borderColor: shouldBeFlagged ? "error.main" : "divider"
      }}
    >
      <Typography fontWeight={fontWeights.semibold} sx={{ px: 2, pt: 2 }}>
        {title}
      </Typography>
      <DataTable
        rows={getCoreUIStore().getTableRows(rows)}
        columns={getCoreUIStore().getTableColumns(columns)}
        noRowsLabel={i18n.get("NO_RESULT")}
        getRowId={(row) => row.uniqueId}
        pageSize={100}
        pageSizeOptions={[50, 100]}
        dynamicRowHeight
        showToolbar
        getRowClassName={
          getRowClassName ||
          ((params) =>
            getCoreHistoryStore().getTableRowClassName(
              reduce(() => params.row.gender, "")
            ))
        }
        getCellClassName={getCellClassName || (() => "table-cell-class")}
      />
    </Paper>
  );
});

export default KhattabatDataTable;
